import {
  type DocumentMeta,
  useSelectionManager,
} from "@prismicio/editor-fields"
import { pluralize } from "@prismicio/editor-support/String"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  ErrorBoundary,
  IconButton,
  Text,
  Tooltip,
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import { deleteDocumentPermission } from "../../app/services/permissions"
import {
  getTeamSpaceByCustomTypeId,
  useResolveMyRole,
  useTeamSpaces,
} from "../../app/services/role"
import type { BulkAction } from "./BulkActionsProvider"
import { useBulkActionsButton } from "./useBulkActionsButton"

export function DeleteDocumentsButton() {
  return (
    <ErrorBoundary renderError={Fallback}>
      <Suspense fallback={<Fallback />}>
        <DeleteButton />
      </Suspense>
    </ErrorBoundary>
  )
}

function DeleteButton() {
  const [open, setOpen] = useState(false)
  const { disabled, tooltip, action, selectedDocuments } = useBulkDeleteButton()

  return (
    <>
      <Tooltip content={tooltip || "Delete"}>
        <IconButton
          icon="delete"
          onClick={() => setOpen(true)}
          disabled={disabled}
          color="grey"
        />
      </Tooltip>
      <DeleteDocumentsConfirmationDialog
        open={open}
        onOpenChange={setOpen}
        action={action}
        selectedDocuments={selectedDocuments}
      />
    </>
  )
}

interface DeleteDocumentsConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: BulkAction
  selectedDocuments: DocumentMeta[]
}

function DeleteDocumentsConfirmationDialog(
  props: DeleteDocumentsConfirmationDialogProps,
) {
  const { open, onOpenChange, action, selectedDocuments } = props

  const { selectedCount } = useSelectionManager()

  function onDelete() {
    const items = selectedDocuments.flatMap((document) =>
      document.versions
        // ensure all archived versions are deleted at once
        // by mapping them as individual documents
        .filter((version) => version.status === "archived")
        .map((version) => ({
          documentId: document.id,
          versionId: version.version_id ?? "",
          status: version.status ?? "",
        })),
    )

    action("delete", { items })
    onOpenChange(false)
  }

  const maybePluralDocument = pluralize(selectedCount, "document")

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title={`Do you really want to delete the selected ${maybePluralDocument}?`}
      />

      <DialogContent>
        <DialogDescription asChild>
          <Box padding={16} flexDirection="column">
            <Text>
              You are about to delete the selected {maybePluralDocument}:{" "}
            </Text>
            <ul>
              {selectedDocuments.map((document, index) => (
                <li key={index}>
                  <Text variant="emphasized">
                    {document.title} ({document.custom_type_id})
                  </Text>
                </li>
              ))}
            </ul>
          </Box>
        </DialogDescription>
      </DialogContent>

      <DialogActions>
        <DialogCancelButton>No</DialogCancelButton>
        <DialogActionButton onClick={onDelete} color="tomato">
          Yes, I want to delete the selected {maybePluralDocument}
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function Fallback() {
  return <IconButton icon="delete" disabled color="grey" />
}

function useBulkDeleteButton() {
  const {
    action,
    disabled: initialDisabled,
    tooltip: initialTooltip,
    selectedDocuments,
  } = useBulkActionsButton()

  const resolveMyRole = useResolveMyRole()
  const teamSpaces = useTeamSpaces()

  const canDeleteDocuments = selectedDocuments.every((document) => {
    const teamSpace = getTeamSpaceByCustomTypeId(
      teamSpaces,
      document.custom_type_id,
    )
    const role = resolveMyRole({
      localeId: document.language.id,
      teamSpaceId: teamSpace.id,
    })

    return deleteDocumentPermission[role]
  })

  const disabled = initialDisabled || !canDeleteDocuments

  function getTooltip() {
    if (initialTooltip) {
      return initialTooltip
    }
    if (!canDeleteDocuments) {
      return "You don't have permission to delete some of the selected documents"
    }
    return undefined
  }

  return {
    action,
    disabled,
    tooltip: getTooltip(),
    selectedDocuments,
  }
}
